import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./TravelerList.module.css";
import AddTravelerModal from "../../../components/Customers/AddTravelerModal";
import {
  useTravelersList,
  useCreateTraveler,
  useDeleteTraveler,
} from "../../../api/people/usePeopleApi";
import { useToast } from "../../../context/ToastContext";

// ── Helpers: API response → UI format ─────────────────────────────────────
const mapApiToUi = (t) => {
  const age = t.age ?? (t.dateOfBirth ? (() => {
    const today = new Date();
    const birth = new Date(t.dateOfBirth);
    let a = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) a--;
    return a;
  })() : null);
  const ageTypeToLabel = { INF: "Infant", CHD: "Child", ADT: "Adult" };
  const travelerType = ageTypeToLabel[t.ageType] ?? (age !== null
    ? (age <= 1 ? "Infant" : age <= 11 ? "Child" : "Adult")
    : "Unknown");
  const linkedCustomerId = t.linkedCustomerId;
  const bookings = Array.isArray(t.bookings) ? t.bookings : [];
  return {
    id: t.id,
    ref: t.travellerCode ?? `TRVL-${String(t.id).padStart(5, "0")}`,
    firstName: t.firstName ?? "",
    lastName: t.lastName ?? "",
    dob: t.dateOfBirth ?? t.dob,
    nationality: t.nationality ?? "",
    gender: t.gender ?? "",
    bookingsCount: bookings.length,
    lastBookingRef: bookings[0]?.ref ?? "",
    age,
    travelerType,
    createdAt: t.createdAt
      ? new Date(t.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
  };
};

const getTravelerType = (age) => {
  if (age === null) return { label: "Unknown", color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb" };
  if (age <= 1)     return { label: "Infant",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" };
  if (age <= 11)    return { label: "Child",   color: "#d97706", bg: "#fff7ed", border: "#fed7aa" };
  return              { label: "Adult",   color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
};

const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(dob);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const avatarColor = (id) => {
  const colors = ["#f5a51e","#059669","#2563eb","#9333ea","#dc2626","#0891b2","#d97706","#16a34a"];
  return colors[(id ?? 0) % colors.length];
};

const TYPE_TABS = [
  { key: "All",    label: "All Travelers" },
  { key: "Adult",  label: "Adults"  },
  { key: "Child",  label: "Children" },
  { key: "Infant", label: "Infants" },
];

const TAB_TO_API = { All: undefined, Adult: "ADT", Child: "CHD", Infant: "INF" };

const TravelerList = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [activeTab,         setActiveTab]         = useState("All");
  const [searchTerm,        setSearchTerm]        = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("all");
  const [modalOpen,         setModalOpen]         = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: apiData, pagination, loading, error, refetch } = useTravelersList({
    q: searchTerm || undefined,
    tab: TAB_TO_API[activeTab],
    nationality: nationalityFilter === "all" ? undefined : nationalityFilter,
    page,
    limit,
  });
  const { execute: createTraveler, loading: saving, error: createError, clearError } = useCreateTraveler();
  const { execute: deleteTraveler, loading: deleting } = useDeleteTraveler();
  const [confirmDeleteRef, setConfirmDeleteRef] = useState(null);

  const travelers = useMemo(() => (apiData ?? []).map(mapApiToUi), [apiData]);

  const nationalities = useMemo(() =>
    Array.from(new Set(travelers.map(t => t.nationality))).filter(Boolean).sort(), [travelers]);

  const tabCounts = useMemo(() => {
    const total = pagination?.total ?? 0;
    const counts = { All: total, Adult: "—", Child: "—", Infant: "—" };
    if (activeTab !== "All") counts[activeTab] = total;
    return counts;
  }, [pagination?.total, activeTab]);

  const hasActiveFilters = searchTerm !== "" || nationalityFilter !== "all";

  const filtered = travelers;

  const handleClear = () => {
    setSearchTerm("");
    setNationalityFilter("all");
    setPage(1);
  };

  const handleAddClick = () => {
    clearError?.();
    setModalOpen(true);
  };

  const handleSave = useCallback(
    async (payload) => {
      try {
        const ageType = payload.travelerType === "Infant" ? "INF"
          : payload.travelerType === "Child" ? "CHD" : "ADT";
        await createTraveler({
          title: payload.title,
          firstName: payload.firstName,
          lastName: payload.lastName,
          dateOfBirth: payload.dob,
          gender: payload.gender,
          nationality: payload.nationality,
          ageType,
        });
        setModalOpen(false);
        showToast("Traveler created successfully", "success");
        refetch();
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Failed to create traveler";
        showToast(msg, "error");
      }
    },
    [createTraveler, refetch, showToast]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!confirmDeleteRef) return;
    try {
      await deleteTraveler(confirmDeleteRef);
      showToast("Traveler deleted successfully", "success");
      setConfirmDeleteRef(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to delete traveler";
      showToast(msg, "error");
      setConfirmDeleteRef(null);
    }
  }, [confirmDeleteRef, deleteTraveler, refetch, showToast]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, nationalityFilter, activeTab]);

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  return (
    <div className={styles.container}>

      {/* ── Error Banner ── */}
      {(error || createError) && (
        <div className={styles.errorBanner}>
          {error || createError}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>✈️</div>
          <div className={styles.headerText}>
            <h1>Traveler Profiles</h1>
            <p>Physical persons who travel — age classified automatically from date of birth</p>
          </div>
        </div>
        <button className={styles.addBtn} onClick={handleAddClick} disabled={saving}>
          {saving ? "Saving..." : "+ Add Traveler"}
        </button>
      </div>

      {/* ── Business Rule Notice ── */}
      <div className={styles.ruleNotice}>
        <span>⚠️</span>
        <span>
          <strong>Business Rule:</strong> A Traveler is never a Customer. Travelers never pay — all financial data is at Customer level only.
          Age is always calculated from date of birth — never entered manually.
        </span>
      </div>

      {/* ── Type Tabs ── */}
      <div className={styles.tabBar}>
        {TYPE_TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""} ${
                isActive && tab.key === "Adult"  ? styles.tabActiveBlue   : ""} ${
                isActive && tab.key === "Child"  ? styles.tabActiveAmber  : ""} ${
                isActive && tab.key === "Infant" ? styles.tabActiveGreen  : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.key === "Adult"  ? "🧑" :
               tab.key === "Child"  ? "👦" :
               tab.key === "Infant" ? "👶" : "👥"} {tab.label}
              <span className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : ""}`}>
                {tabCounts[tab.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Filter Bar ── */}
      <div className={styles.filterBar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by ref or name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filterDivider} />
        <div className={styles.filterGroup}>
          <label>Nationality:</label>
          <select value={nationalityFilter} onChange={e => setNationalityFilter(e.target.value)}>
            <option value="all">All</option>
            {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={handleClear}>Clear Filters</button>
        )}
      </div>

      {/* ── Results Summary ── */}
      <div className={styles.resultsSummary}>
        {loading ? (
          "Loading travelers..."
        ) : (
          <>
            Showing {filtered.length} of {pagination?.total ?? filtered.length} travelers
            {hasActiveFilters && <span className={styles.filtered}>(filtered)</span>}
          </>
        )}
      </div>

      {/* ── Table ── */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Traveler</th>
            <th>Type / Age</th>
            <th>Date of Birth</th>
            <th>Nationality</th>
            <th>Bookings</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>⏳</div>
                  <p className={styles.emptyTitle}>Loading travelers...</p>
                </div>
              </td>
            </tr>
          ) : filtered.length > 0 ? filtered.map(t => {
            const typeStyle = getTravelerType(t.age);

            return (
              <tr key={t.id}>

                {/* Traveler */}
                <td>
                  <div className={styles.travelerCell}>
                    <div className={styles.avatar} style={{ background: avatarColor(t.id) }}>
                      {(t.firstName ?? "")[0]}{(t.lastName ?? "")[0]}
                    </div>
                    <div>
                      <div className={styles.travelerName}>{t.firstName} {t.lastName}</div>
                      <div className={styles.travelerRef}>{t.ref}</div>
                      <div className={styles.travelerSince}>Since {t.createdAt}</div>
                    </div>
                  </div>
                </td>

                {/* Type / Age */}
                <td>
                  <div className={styles.typeCell}>
                    <span
                      className={styles.typeBadge}
                      style={{ background: typeStyle.bg, color: typeStyle.color, border: `1px solid ${typeStyle.border}` }}
                    >
                      {t.travelerType === "Adult"  ? "🧑" :
                       t.travelerType === "Child"  ? "👦" :
                       t.travelerType === "Infant" ? "👶" : "❓"}
                      {" "}{t.travelerType}
                    </span>
                    <span className={styles.ageLabel}>
                      {t.age !== null ? `${t.age} yrs` : "—"}
                    </span>
                  </div>
                </td>

                {/* DOB */}
                <td>
                  <div className={styles.dobValue}>{formatDob(t.dob)}</div>
                  <div className={styles.dobCalc}>⚡ System calculated</div>
                </td>

                {/* Nationality */}
                <td>
                  <span className={styles.nationalityBadge}>{t.nationality}</span>
                </td>

                {/* Bookings */}
                <td>
                  {t.bookingsCount > 0
                    ? <span className={styles.bookingCount}>{t.bookingsCount} booking{t.bookingsCount !== 1 ? "s" : ""}</span>
                    : <span className={styles.noBookings}>No bookings</span>}
                </td>

                {/* Actions */}
                <td>
                  <div className={styles.rowActions}>
                    <button
                      className={styles.btnOpen}
                      onClick={() => navigate(`/people/travelers/${t.ref}`, { state: { traveler: t } })}
                    >
                      Open
                    </button>
                    <button
                      className={styles.btnDelete}
                      onClick={() => setConfirmDeleteRef(t.ref)}
                      disabled={deleting}
                    >
                      Delete
                    </button>
                  </div>
                </td>

              </tr>
            );
          }) : (
            <tr>
              <td colSpan="6">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>✈️</div>
                  <p className={styles.emptyTitle}>No travelers found</p>
                  <p className={styles.emptySubtitle}>Try adjusting your filters or search term</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── Pagination ── */}
      {(pagination?.totalPages ?? 1) > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Page {pagination?.page ?? page} of {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} total)
          </span>
          <div className={styles.pageNumbers}>
            {pageNumbers.map((p, idx) =>
              p === "…" ? (
                <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {confirmDeleteRef && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <div className={styles.confirmIcon}>⚠️</div>
            <h3 className={styles.confirmTitle}>Delete Traveler?</h3>
            <p className={styles.confirmText}>
              This will permanently delete <strong>{confirmDeleteRef}</strong> and all associated data. This cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmCancel}
                onClick={() => setConfirmDeleteRef(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className={styles.confirmDelete}
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AddTravelerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
};

export default TravelerList;